/**
 * The signed-in user's profile read (AuthContext), widened for the user menu with the primary site's
 * name and the assigned sites (USR-003). The site part is display only: if the widened read fails
 * (an embed refused), the plain read runs again without it, so it can never block sign-in or a page.
 */

export const PROFILE_COLUMNS = 'role, full_name, organization_id, must_change_password';

// Both FKs are named: profile_site_assignments has two FKs to profiles (profile_id, assigned_by).
export const PROFILE_SITE_COLUMNS =
  'default_site:sites!profiles_default_site_id_fkey(name), ' +
  'site_assignments:profile_site_assignments!profile_site_assignments_profile_id_fkey(site_id)';

export interface ProfileSite {
  primarySiteName: string | null;
  assignedSiteCount: number;
}

/** The primary site's name and the number of distinct assigned sites; null / 0 when absent. */
export function profileSiteOf(data: unknown): ProfileSite {
  const d = (data ?? {}) as { default_site?: unknown; site_assignments?: unknown };
  const ds = (Array.isArray(d.default_site) ? d.default_site[0] : d.default_site) as
    | { name?: unknown }
    | null
    | undefined;
  const name = typeof ds?.name === 'string' && ds.name.trim() ? ds.name.trim() : null;
  const rows = Array.isArray(d.site_assignments)
    ? (d.site_assignments as Array<{ site_id?: unknown }>)
    : [];
  const sites = new Set(
    rows.map((r) => r?.site_id).filter((s): s is string => typeof s === 'string' && s !== '')
  );
  return { primarySiteName: name, assignedSiteCount: sites.size };
}

/** The browser Supabase client, loosely typed (its own types are too deep to match structurally). */
export interface ProfileClient {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  from(relation: string): any;
}

export interface ProfileRead {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  error: unknown;
  site: ProfileSite;
}

const NO_SITE: ProfileSite = { primarySiteName: null, assignedSiteCount: 0 };

/** The widened read; on any error, the plain read once more, with no site. Never throws for a site failure. */
export async function readProfile(client: ProfileClient, userId: string): Promise<ProfileRead> {
  const wide = await client
    .from('profiles')
    .select(`${PROFILE_COLUMNS}, ${PROFILE_SITE_COLUMNS}`)
    .eq('id', userId)
    .single();
  if (!wide.error && wide.data)
    return { data: wide.data, error: null, site: profileSiteOf(wide.data) };
  const plain = await client.from('profiles').select(PROFILE_COLUMNS).eq('id', userId).single();
  return { data: plain.data ?? null, error: plain.error ?? null, site: NO_SITE };
}
